import { Formik } from "formik";
import { useRef, useState } from "react";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormSelect from "../formFields/CMSFormSelect";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import success from "../../assets/images/success.png";
import { useToast } from "../contexts/ToastContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { countries, nationalities } from "../utils/countryHelper";
import { signupSchema } from "../utils/validations";
import axios from "../../config/axios";
import FormButton from "../common/FormButton";
import { useGeneral } from "../contexts/GeneralContext";
import {
  formatToLabelValue,
  formatToSameLabelValue,
} from "../utils/commonHelper";
import { useCart } from "../contexts/CartContext";

const SignupForm = () => {
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState("");
  const { cart,fetchCart } = useCart();
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();
  const { extraContent } = useGeneral();
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    confirmEmail: useRef(null),
    nationality: useRef(null),
    phone: useRef(null),
    password: useRef(null),
    confirmPassword: useRef(null),
  };
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get("from");

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;
    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  const checkEmailRegistered = async (email) => {
    if (!email) return false;
    try {
      setCheckingEmail(true);
      const res = await axios.post("dashboard/customers/is-email-registered", {
        email,
      });
      return res.data?.registered; // true if email exists
    } catch (err) {
      console.error("Email check failed", err);
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    setStatus("loading");

    try {
      // Final email check before submitting
      // const isRegistered = await checkEmailRegistered(values.email);
      // if (isRegistered) {
      //   addToast("This email is already registered.", "error");
      //   setStatus("idle");
      //   setSubmitting(false);
      //   return;
      // }

      // Call signup API
      const res = await axios.post("dashboard/customers/signup", values);
      setStatus("success");
      resetForm();
      setIsLoggedIn(true);
      localStorage.removeItem("promoCodeSaved");
      localStorage.removeItem("selectedCity");
      localStorage.setItem("loggedInUser", JSON.stringify(res.data?.user));

      setTimeout(() => {
        if (from === "cart") {
          fetchCart();
          navigate(`/checkout/order`);
        } else if (from === "plan") {
          navigate(`/subscribe/child`);
        } else {
          navigate("/account");
        }
      }, 10000);
    } catch (err) {
      console.error(err);
      setError("Signup failed. Please try again.");
      setTimeout(() => {
        setError("");
      }, 5000);
      setStatus("idle");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-34pt">Create Account</p>
      <div
        className={
          status === "success"
            ? "grid grid-cols-1"
            : "grid lg:grid-cols-[2fr_1fr] gap-12"
        }
      >
        <div
          className={`mt-4 lg:mt-6  rounded-[15px] relative min-h-[300px] transition-colors ${
            status === "success"
              ? "bg-secondary p-10 2.5xl:p-20 "
              : "bg-[#DEE2DD]/50 p-10 lg:p-20 "
          }`}
        >
          {status === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
              <ContainerLoading />
            </div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ x: -200, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -200, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="grid grid-cols-1 gap-8 items-start">
                <div className="w-full rounded-[15px] p-0 md:px-10 md:py-10 2xl:p-5 ">
                  <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-2 lg:gap-10 ">
                    <div className=" md:p-5 flex flex-col justify-between order-2 lg:order-1 ">
                      <p className="text-white text-28pt md:text-48pt leading-none mb-8 text-center lg:text-left">
                        Account Created Successfully!
                      </p>
                      <p className="text-orange text-[25px] md:text-[34px] leading-none text-center lg:text-left mb-8">
                        Welcome to Nutricub! We’re thrilled to have you join our
                        parent community. Thank you for creating your account.
                      </p>
                      {from != "cart" && from != "plan" && (
                        <p className="text-orange text-[20px]  leading-tight text-center lg:text-left">
                          Please wait a moment, you’ll be redirected to your
                          dashboard shortly.
                        </p>
                      )}
                      {from === "cart" && (
                        <p className="text-orange text-[20px]  leading-tight text-center lg:text-left">
                          Please wait a moment, you’ll be redirected to the
                          checkout shortly.
                        </p>
                      )}
                      {from === "plan" && (
                        <p className="text-orange text-[20px]  leading-tight text-center lg:text-left">
                          Please wait a moment, you’ll be redirected to
                          subscription process shortly.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-center order-1 lg:order-2">
                      <img
                        src={success}
                        alt="success"
                        className="w-[80px] h-[80px] md:w-[110px] md:h-[110px] lg:w-[134px] lg:h-[134px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {status !== "success" && (
            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                email: "",
                confirmEmail: "",
                nationality: "",
                mobile: "",
                password: "",
                confirmPassword: "",
                cartId:
                  from === "cart" && cart?.orderCart?._id
                    ? cart?.orderCart?._id || ""
                    : "",
              }}
              validationSchema={signupSchema}
              onSubmit={handleSubmit}
            >
              {(form) => (
                <div className="w-full grid lg:grid-cols-1 2xl:grid-cols-1 gap-10 ">
                  <div className="w-full grid grid-cols-1 gap-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ">
                      <CMSFormInput
                        ref={refs.firstName}
                        name="firstName"
                        label="First Name"
                        className="1"
                        form={form}
                        required
                      />
                      <CMSFormInput
                        ref={refs.lastName}
                        name="lastName"
                        label="Last Name"
                        className="1"
                        form={form}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <CMSFormInput
                        ref={refs.email}
                        name="email"
                        label="Email"
                        className="1"
                        form={form}
                        required
                      />

                      <CMSFormInput
                        ref={refs.confirmEmail}
                        name="confirmEmail"
                        label="Confirm Email Address"
                        form={form}
                        className="1"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <CMSFormTelephone
                        ref={refs.phone}
                        name="mobile"
                        label="Phone"
                        className="1"
                        form={form}
                        required
                      />
                      <CMSFormSelect
                        ref={refs.nationality}
                        name="nationality"
                        label="Nationality"
                        className="1"
                        placeholder={{ label: "Nationality", value: "" }}
                        form={form}
                        onChange={form.handleChange}
                        required
                        options={formatToSameLabelValue(
                          extraContent?.nationalities
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <CMSFormInput
                        ref={refs.password}
                        name="password"
                        label="Password"
                        className="1"
                        type="password"
                        form={form}
                        required
                      />
                      <CMSFormInput
                        ref={refs.confirmPassword}
                        name="confirmPassword"
                        label="Confirm Password"
                        className="1"
                        type="password"
                        form={form}
                        required
                      />
                    </div>
                    {error && (
                      <div
                        className={`relative transition-all duration-300 ease-in-out ${
                          error
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-2"
                        }`}
                      >
                        {error && <span className="errorMessage">{error}</span>}
                      </div>
                    )}
                    <div className="flex items-end justify-end mt-6">
                      <FormButton
                        text="Submit"
                        type="submit"
                        className="text-lg px-4"
                        onClick={() => {
                          form.handleSubmit();
                          setTimeout(
                            () => scrollToFirstError(form?.errors),
                            100
                          );
                        }}
                        disabled={checkingEmail || form.isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              )}
            </Formik>
          )}
        </div>
        <div
          className={
            status === "success"
              ? "hidden"
              : "hidden lg:flex items-center justify-center"
          }
        >
          <img
            src={require("../../assets/images/create-account2.png")}
            className="w-full h-auto max-w-[320px]"
          />
        </div>
      </div>
    </>
  );
};

export default SignupForm;
